import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { movies } from "@/mocks/movies";

const hotKeywords = ["冷色调", "初雪之后", "红色高跟鞋", "汉江之夏", "镜头语言", "申允浩"];

const hotMovies = [...movies].sort((a, b) => b.views - a.views).slice(0, 4);

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keywordParam = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(keywordParam);

  useEffect(() => {
    setKeyword(keywordParam);
  }, [keywordParam]);

  const query = keywordParam.trim().toLowerCase();

  const results = useMemo(() => {
    if (!query) return [];
    return movies.filter((movie) => {
      const haystack = [
        movie.title,
        movie.originalTitle,
        movie.director,
        movie.cast.join(" "),
        movie.genres.join(" "),
        movie.synopsis,
        movie.tags.join(" "),
        String(movie.year),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [query]);

  usePageMeta(
    query ? `「${keywordParam}」搜索结果 - 韩国三级电影` : "搜索韩国三级电影 - 影片资料检索",
    query
      ? `关于「${keywordParam}」的韩国影视资料检索结果，共 ${results.length} 部相关作品，包含影片简介与解析入口。`
      : "输入片名、导演或演员关键词，检索站内韩国影视资料，支持按类型、年份筛选浏览。",
    "韩国三级电影搜索,影片检索,韩国电影资料",
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
  };

  const runKeyword = (word: string) => {
    setKeyword(word);
    setSearchParams({ q: word });
  };

  const clearKeyword = () => {
    setKeyword("");
    setSearchParams({});
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/60 bg-background-100/40">
        <div className="mx-auto w-full max-w-[1360px] px-4 pb-8 pt-6 md:px-8 md:pb-10 md:pt-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

          <div className="mt-5 max-w-3xl">
            <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
              Search
            </span>
            <h1 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[34px]">
              搜索韩国三级电影资料
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-500">
              支持按片名、原文片名、导演、演员、类型与标签检索。搜索结果为站内演示数据，实时在前端完成匹配。
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            role="search"
            className="mt-6 flex w-full max-w-3xl flex-col gap-2 rounded-lg border border-background-300/70 bg-background-50 p-2 sm:flex-row sm:items-center"
          >
            <div className="flex flex-1 items-center gap-2.5 px-3">
              <i className="ri-search-line text-base text-foreground-500"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入关键词，例如：冷色调 / 申允浩 / 悬疑"
                aria-label="搜索关键词"
                className="h-11 w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-500"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={clearKeyword}
                  aria-label="清空关键词"
                  className="flex cursor-pointer items-center justify-center text-foreground-500 transition-colors hover:text-foreground-900"
                >
                  <i className="ri-close-circle-fill text-base"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-11 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="text-foreground-600">热门关键词：</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => runKeyword(word)}
                className={`cursor-pointer rounded-full border px-2.5 py-1 transition-colors ${
                  keywordParam === word
                    ? "border-accent-500 bg-accent-500/10 text-accent-400"
                    : "border-background-300/70 hover:border-accent-500 hover:text-accent-400"
                }`}
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1360px] px-4 py-8 md:px-8 md:py-10">
        {query ? (
          <>
            <div className="flex flex-col gap-2 border-b border-background-200/70 pb-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs text-foreground-500">当前关键词</p>
                <h2 className="mt-1 font-heading text-lg font-bold text-foreground-950">
                  「{keywordParam}」
                  <span className="ml-2 text-sm font-normal text-foreground-500">
                    共找到 {results.length} 条结果
                  </span>
                </h2>
              </div>
              <button
                type="button"
                onClick={clearKeyword}
                className="inline-flex w-fit cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-accent-400"
              >
                <i className="ri-refresh-line"></i>
                清空并重新搜索
              </button>
            </div>

            {results.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:grid-cols-4">
                {results.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} showDesc />
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到相关影片"
                  desc={`站内暂时没有与「${keywordParam}」匹配的资料。可以换个片名、导演名，或者从下方热门作品开始浏览。`}
                  resetLabel="清空关键词"
                  onReset={clearKeyword}
                />

                <div className="mt-10">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="h-3 w-[3px] rounded-full bg-accent-500"></span>
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      也许你会感兴趣
                    </h2>
                  </div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                    {hotMovies.map((movie, index) => (
                      <MovieCard key={movie.id} movie={movie} index={index} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-12 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-line text-2xl"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                输入关键词开始检索
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-500">
                可以搜索片名、导演、演员或类型标签。也可以直接浏览下方热门作品，或按分类进入完整资料库。
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                <Link
                  to="/category"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
                >
                  浏览全部分类
                </Link>
                <Link
                  to="/category?c=classic"
                  className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  经典收藏
                </Link>
              </div>
            </div>

            <div className="mt-10">
              <div className="mb-4 flex items-end justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-[3px] rounded-full bg-accent-500"></span>
                    <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
                      Trending now
                    </span>
                  </div>
                  <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950">
                    当前热门影片
                  </h2>
                </div>
                <Link
                  to="/category?c=hot"
                  className="group inline-flex items-center gap-1 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-accent-400"
                >
                  热度榜单
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
                {hotMovies.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} index={index} showDesc />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </SiteLayout>
  );
}